import { useState } from 'react';

interface Review {
  id: number;
  name: string;
  rating: number;
  date: string;
  text: string;
  avatar: string;
}

export default function GoogleReviews() {
  const reviews: Review[] = [
    {
      id: 1,
      name: 'Sarah Mitchell',
      rating: 5,
      date: '2 weeks ago',
      text: 'I have been terrified of dentists my whole life, but Dr. Saeed and his team completely changed that. They were so patient and understanding. The sedation option made everything so much easier. I actually look forward to my appointments now!',
      avatar: 'https://public.readdy.ai/ai/img_res/23297003759e32e5d0b520bcafcd57dc.jpg'
    },
    {
      id: 2,
      name: 'James Thompson',
      rating: 5,
      date: '1 month ago',
      text: 'After avoiding the dentist for 10 years due to anxiety, I finally found Beaulieu Dental. The team is incredibly compassionate and never made me feel judged. They explained everything clearly and let me take breaks whenever I needed. Highly recommend!',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20man%20smiling%20headshot%20portrait%20confident%20friendly%20natural%20lighting&width=100&height=100&seq=review-avatar-james&orientation=squarish'
    },
    {
      id: 3,
      name: 'Emma Richardson',
      rating: 5,
      date: '3 weeks ago',
      text: 'The best dental experience I have ever had. Dr. Yasir and the team are so gentle and caring. They took the time to understand my fears and worked with me at my own pace. The calming environment really helps too. Thank you!',
      avatar: 'https://readdy.ai/api/search-image?query=young%20woman%20smiling%20headshot%20portrait%20cheerful%20warm%20natural%20lighting&width=100&height=100&seq=review-avatar-emma&orientation=squarish'
    },
    {
      id: 4,
      name: 'Michael Brown',
      rating: 5,
      date: '2 months ago',
      text: 'I cannot thank Dr. Saeed enough. I had severe dental anxiety and he made me feel completely at ease. The sedation dentistry option was perfect for me. The whole team is professional, kind, and truly cares about their patients.',
      avatar: 'https://readdy.ai/api/search-image?query=middle%20aged%20man%20smiling%20headshot%20portrait%20professional%20friendly%20natural%20lighting&width=100&height=100&seq=review-avatar-michael&orientation=squarish'
    },
    {
      id: 5,
      name: 'Lisa Anderson',
      rating: 5,
      date: '1 week ago',
      text: 'Beaulieu Dental is amazing for anxious patients. They never rush you and always check if you are comfortable. The practice is beautiful and calming. I finally feel like I have found a dental home where I am understood and cared for.',
      avatar: 'https://readdy.ai/api/search-image?query=woman%20smiling%20headshot%20portrait%20happy%20relaxed%20natural%20lighting&width=100&height=100&seq=review-avatar-lisa&orientation=squarish'
    },
    {
      id: 6,
      name: 'David Wilson',
      rating: 5,
      date: '3 months ago',
      text: 'Outstanding care from start to finish. Dr. Yasir is incredibly skilled and patient. The team explained every step and made sure I was comfortable throughout. If you are nervous about dental treatment, this is the place to go!',
      avatar: 'https://readdy.ai/api/search-image?query=senior%20man%20smiling%20headshot%20portrait%20confident%20friendly%20natural%20lighting&width=100&height=100&seq=review-avatar-david&orientation=squarish'
    }
  ];

  const [hoveredReview, setHoveredReview] = useState<number | null>(null);

  return (
    <section id="testimonials" className="py-20 bg-gradient-to-br from-white via-[#f8f9f8] to-white relative overflow-hidden">
      {/* Decorative Background Elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-[#d0a24f]/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#dcb964]/5 rounded-full blur-3xl"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-3 bg-gradient-to-r from-[#d0a24f] to-[#dcb964] text-white px-8 py-3 rounded-full mb-6 shadow-lg">
            <i className="ri-google-fill text-3xl"></i>
            <span className="text-2xl font-bold">Google Reviews</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-[#444c42] mb-4">
            What Our Patients Say
          </h2>
          <p className="text-lg text-[#7A8178] max-w-2xl mx-auto leading-relaxed">
            Real stories from real patients who overcame their dental anxiety with our help
          </p>
          
          {/* Rating Summary */}
          <div className="flex items-center justify-center gap-4 mt-8">
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <i key={star} className="ri-star-fill text-3xl text-[#d0a24f]"></i>
              ))}
            </div>
            <div className="text-left">
              <div className="text-3xl font-bold text-[#444c42]">5.0</div>
              <div className="text-sm text-[#7A8178]">Based on 150+ reviews</div>
            </div>
          </div>
        </div>

        {/* Reviews Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {reviews.map((review) => (
            <div
              key={review.id}
              className={`bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 cursor-pointer border-2 ${
                hoveredReview === review.id ? 'border-[#d0a24f]' : 'border-transparent'
              }`}
              onMouseEnter={() => setHoveredReview(review.id)}
              onMouseLeave={() => setHoveredReview(null)}
            >
              {/* Google Icon */}
              <div className="flex items-center justify-between mb-4">
                <i className="ri-google-fill text-3xl text-[#d0a24f]"></i>
                <span className="text-sm text-[#7A8178]">{review.date}</span>
              </div>

              {/* Stars */}
              <div className="flex items-center gap-1 mb-4">
                {[1, 2, 3, 4, 5].map((star) => (
                  <i key={star} className="ri-star-fill text-xl text-[#d0a24f]"></i>
                ))}
              </div>

              {/* Review Text */}
              <p className="text-[#7A8178] leading-relaxed mb-6 text-sm">
                "{review.text}"
              </p>

              {/* Reviewer Info */}
              <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
                <img
                  src={review.avatar}
                  alt={`${review.name} - Beaulieu Dental patient review`}
                  title={`${review.name} review`}
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div>
                  <div className="font-semibold text-[#444c42]">{review.name}</div>
                  <div className="text-xs text-[#7A8178]">Verified Patient</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Button */}
        <div className="text-center mt-12">
          <a
            href="https://www.google.com/maps/place/Beaulieu+Dental/@51.7234,-0.4789,17z/data=!4m7!3m6!1s0x0:0x0!8m2!3d51.7234!4d-0.4789!9m1!1b1"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-[#d0a24f] to-[#dcb964] text-white px-8 py-4 rounded-full font-semibold text-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-google-fill text-xl"></i>
            Read More Reviews on Google
            <i className="ri-external-link-line text-xl"></i>
          </a>
        </div>
      </div>
    </section>
  );
}
